import React, {
  memo,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import * as Sentry from '@sentry/react';
import { useParams } from 'react-router-dom';
import { useQueryParam } from 'use-query-params';

import {
  DisplayConfigURLParam,
  getUserDisplayConfig,
  savedConfigsKey,
  saveUserDisplayConfig
} from './utils';

type WorkflowOverviewContextType = {
  selectedTraceIds: never[];
  setSelectedTraceIds: React.Dispatch<React.SetStateAction<never[]>>;
  showNewTracesTray: boolean;
  toggleNewTracesTray: () => void;
  showTracesUpdateTray: boolean;
  toggleUpdateTracesTray: () => void;
  archiveDisplayTraces: boolean;
  toggleArchiveDisplayTraces: () => void;
  setArchiveDisplayTraces: React.Dispatch<React.SetStateAction<boolean>>;
  userDisplayConfig: Record<string, unknown>;
  setUserDisplayConfig: React.Dispatch<React.SetStateAction<unknown>>;
  savedViewsArray: unknown[];
  setsavedViewsArray: React.Dispatch<React.SetStateAction<unknown[]>>;
  updateSavedViewsArray: (newValue: unknown[]) => void;
  workflowId: string;
};

const WorkflowOverviewContext =
  React.createContext<Maybe<WorkflowOverviewContextType>>(undefined);

/** @deprecated Use useWorkflowOverviewContext hook instead */
export const withWorkflowOverviewContext = Component => props => (
  <WorkflowOverviewContext.Consumer>
    {ctx => <Component {...props} context={ctx} />}
  </WorkflowOverviewContext.Consumer>
);

export const useWorkflowOverviewContext = () => {
  const context = useContext(WorkflowOverviewContext);

  if (!context) {
    const error = new Error(
      'useWorkflowOverviewContext must be used within a WorkflowOverviewContextProvider'
    );
    console.error(error);
    Sentry.captureException(error);
    throw error;
  }

  return context;
};
export const WorkflowOverviewContextProvider = memo(
  ({ children }: PropsWithChildren) => {
    const { id: workflowId } = useParams<{ id: string }>();

    // 'displayConfig' url query parameter as state variable
    const [queryStringDisplayConfig, setQueryStringDisplayConfig] =
      useQueryParam('displayConfig', DisplayConfigURLParam);

    // Ref boolean value to make sure the following code is only executed once, before the component will render its children
    const willMount = useRef(true);
    // If it's the first execution...
    if (willMount.current) {
      // ...and if a config is available in the URL,
      // we save it in the local storage to ensure that the overview table (that will render inside this context) uses it when fetching the intial data.
      if (queryStringDisplayConfig)
        saveUserDisplayConfig(workflowId, queryStringDisplayConfig);
      // Update ref to prevent the above block to be executed on the next render
      willMount.current = false;
    }

    const [selectedTraceIds, setSelectedTraceIds] = useState([]);
    const [showNewTracesTray, setNewTracesTray] = useState(false);
    const [showTracesUpdateTray, setUpdateTracesTray] = useState(false);
    const [archiveDisplayTraces, setArchiveDisplayTraces] = useState(false);
    const toggleArchiveDisplayTraces = useCallback(
      () => setArchiveDisplayTraces(!archiveDisplayTraces),
      [archiveDisplayTraces, setArchiveDisplayTraces]
    );
    // Initialize userDisplayConfig with value from URL or get it from localStorage
    const [userDisplayConfig, setUserDisplayConfig] = useState(
      queryStringDisplayConfig || getUserDisplayConfig(workflowId)
    );
    const [savedViewsArray, setsavedViewsArray] = useState(
      JSON.parse(
        window.localStorage.getItem(savedConfigsKey(workflowId)) ?? '[]'
      ) || []
    );

    // When userDisplayConfig value changes, repercute the changes to the URL parameter
    useEffect(() => {
      setQueryStringDisplayConfig(userDisplayConfig, 'replaceIn');
    }, [userDisplayConfig]);

    const toggleNewTracesTray = useCallback(
      () => setNewTracesTray(!showNewTracesTray),
      [showNewTracesTray, setNewTracesTray]
    );
    const toggleUpdateTracesTray = useCallback(
      () => setUpdateTracesTray(!showTracesUpdateTray),
      [showTracesUpdateTray, setUpdateTracesTray]
    );

    const savedViewStorageEventHandler = e => {
      setsavedViewsArray(e.detail);
    };

    useEffect(() => {
      window.addEventListener(
        'savedViewsStorage',
        savedViewStorageEventHandler
      );
      return () => {
        window.removeEventListener(
          'savedViewsStorage',
          savedViewStorageEventHandler
        );
      };
    }, [savedViewsArray]);

    const updateSavedViewsArray = newValue => {
      window.localStorage.setItem(
        savedConfigsKey(workflowId),
        JSON.stringify(newValue)
      );
      const event = new CustomEvent('savedViewsStorage', { detail: newValue });

      window.dispatchEvent(event);
    };

    const context = useMemo(
      () => ({
        selectedTraceIds,
        setSelectedTraceIds,
        showNewTracesTray,
        toggleNewTracesTray,
        showTracesUpdateTray,
        toggleUpdateTracesTray,
        archiveDisplayTraces,
        toggleArchiveDisplayTraces,
        setArchiveDisplayTraces,
        userDisplayConfig,
        setUserDisplayConfig,
        savedViewsArray,
        setsavedViewsArray,
        updateSavedViewsArray,
        workflowId
      }),
      [
        selectedTraceIds,
        showNewTracesTray,
        toggleNewTracesTray,
        showTracesUpdateTray,
        toggleUpdateTracesTray,
        archiveDisplayTraces,
        toggleArchiveDisplayTraces,
        userDisplayConfig,
        savedViewsArray,
        workflowId
      ]
    );

    return (
      <WorkflowOverviewContext.Provider value={context}>
        {children}
      </WorkflowOverviewContext.Provider>
    );
  }
);
